import Cookies from "js-cookie";

const STAGING_COOKIE_PREFIX = "stg_";

const getHostname = () =>
  typeof window !== "undefined" ? window.location.hostname : "";

const getAppMode = () => import.meta.env.MODE;

const isStagingHostname = (hostname: string) =>
  hostname.includes("stg.ippon-cloud.com");

const isStagingEnvironment = () =>
  getAppMode() === "staging" || isStagingHostname(getHostname());

const getEnvironmentCookieName = (name: string): string =>
  isStagingEnvironment() ? `${STAGING_COOKIE_PREFIX}${name}` : name;

export const getAuthTokenCookieName = (): string =>
  getEnvironmentCookieName("token");

export const getRefreshTokenCookieName = (): string =>
  getEnvironmentCookieName("refreshToken");

export const getMyUseServiceCookieName = (): string =>
  getEnvironmentCookieName("my_use_service");

// Cookie操作の共通設定
const getCookieOptions = () => {
  const hostname = getHostname();
  const isStaging = isStagingHostname(hostname);
  const isProduction = hostname.includes("ippon-cloud.com") && !isStaging;
  const options: any = {
    path: "/",
  };

  if (isStaging) {
    options.domain = "stg.ippon-cloud.com";
    options.secure = true;
    options.sameSite = "Lax";
  } else if (isProduction) {
    options.domain = "ippon-cloud.com";
    options.secure = true;
    options.sameSite = "Lax";
  }

  return { isProduction, options };
};

// 共通のCookie取得関数
export const getSecureCookie = (name: string): string | null => {
  return Cookies.get(name) || null;
};

// 共通のCookie設定関数
export const setSecureCookie = (
  name: string,
  value: string,
  expires?: number,
): void => {
  const { options } = getCookieOptions();
  if (expires) {
    options.expires = expires;
  }
  Cookies.set(name, value, options);
};

// 共通のCookie削除関数
export const removeSecureCookie = (name: string): void => {
  const { options } = getCookieOptions();
  Cookies.remove(name, options);
};

export const getAuthToken = (): string | null => {
  return getSecureCookie(getAuthTokenCookieName());
};

export const setAuthToken = (value: string, expires?: number): void => {
  const cookieName = getAuthTokenCookieName();
  setSecureCookie(cookieName, value, expires);

  if (cookieName !== "token") {
    removeSecureCookie("token");
  }
};

export const removeAuthToken = (): void => {
  const cookieName = getAuthTokenCookieName();
  removeSecureCookie(cookieName);

  if (cookieName !== "token") {
    removeSecureCookie("token");
  }
};

export const getRefreshToken = (): string | null => {
  return getSecureCookie(getRefreshTokenCookieName());
};

export const setRefreshToken = (value: string, expires?: number): void => {
  const cookieName = getRefreshTokenCookieName();
  setSecureCookie(cookieName, value, expires);

  if (cookieName !== "refreshToken") {
    removeSecureCookie("refreshToken");
  }
};

export const removeRefreshToken = (): void => {
  const cookieName = getRefreshTokenCookieName();
  removeSecureCookie(cookieName);

  if (cookieName !== "refreshToken") {
    removeSecureCookie("refreshToken");
  }
};

export const getMyUseServiceCookie = (): string | null => {
  return getSecureCookie(getMyUseServiceCookieName());
};

export const setMyUseServiceCookie = (
  value: string,
  expires?: number,
): void => {
  const cookieName = getMyUseServiceCookieName();
  setSecureCookie(cookieName, value, expires);

  if (cookieName !== "my_use_service") {
    removeSecureCookie("my_use_service");
  }
};

interface TokenData {
  clientID: string;
  clientName: string;
  employeeID: string;
  role: string;
  email?: string;
  exp?: number;
  [key: string]: any;
}

/**
 * Base64 URLセーフエンコーディングをデコード（UTF-8対応）
 */
const base64UrlDecode = (str: string): string => {
  // パディングを追加
  str += "=".repeat((4 - (str.length % 4)) % 4);
  // URLセーフ文字を標準Base64文字に変換
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");

  // UTF-8デコードのためにTextDecoderを使用
  try {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new TextDecoder("utf-8").decode(bytes);
  } catch (error) {
    // フォールバック: 従来の方法
    return atob(base64);
  }
};

/**
 * JWTトークンをデコードする
 */
const decodeJWT = (token: string): TokenData | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT format");
      return null;
    }

    const payload = parts[1];
    const decodedPayload = base64UrlDecode(payload);
    const parsedPayload = JSON.parse(decodedPayload);

    return parsedPayload;
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

/**
 * JWTトークンの有効期限をチェック
 * 注意: 現在は使用されていませんが、将来的に必要になる可能性があります
 */
// const isTokenExpired = (tokenData: TokenData): boolean => {
//   if (!tokenData.exp) return false;

//   const currentTime = Math.floor(Date.now() / 1000);
//   return tokenData.exp < currentTime;
// };

/**
 * トークンを取得してデコードされたデータを返す
 * 注意: 有効期限チェックは行わない（axios interceptorで処理されるため）
 */
const getTokenData = (): TokenData | null => {
  const token = getAuthToken();
  if (!token) {
    return null;
  }

  const tokenData = decodeJWT(token);
  if (!tokenData) {
    return null;
  }

  // 有効期限チェックはaxios interceptorで事前に処理されるため、
  // ここでは期限切れトークンの削除は行わない
  return tokenData;
};

/**
 * クライアントIDを取得
 */
export const getClientID = (): string | null => {
  const tokenData = getTokenData();
  return (
    tokenData?.["custom:clientId"] ||
    tokenData?.clientID ||
    tokenData?.client_id ||
    null
  );
};

/**
 * 従業員IDを取得
 */
export const getEmployeeID = (): string | null => {
  const tokenData = getTokenData();
  return (
    tokenData?.sub || tokenData?.employeeID || tokenData?.employee_id || null
  );
};

/**
 * ロールを取得
 */
export const getRole = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.["custom:role"] || tokenData?.role || null;
};

/**
 * クライアント名を取得（JWTには含まれていないためAPIから取得が必要）
 */
export const getClientName = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.clientName || tokenData?.client_name || null;
};

/**
 * メールアドレスを取得
 */
export const getEmail = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.email || null;
};

/**
 * 期限切れチェックなしでトークンデータを取得（リフレッシュトークン用）
 */
const getTokenDataWithoutExpCheck = (): TokenData | null => {
  const token = getAuthToken();
  if (!token) {
    return null;
  }

  const tokenData = decodeJWT(token);
  if (!tokenData) {
    return null;
  }

  // 期限切れチェックはしない
  return tokenData;
};

// [RECOVERY] token cookie が expires (1 日) で先に消えた後、refreshToken
// (7 日) だけ残っている状態から復旧するために、login / refresh の度に
// username を localStorage に保存しておく。Cognito の secretHash 計算に
// username が必須なので、token cookie が無いと refresh が出来ない問題を
// 回避する。forceLogout で localStorage.clear() されるため明示ログアウト
// 時は綺麗に消える。
const COGNITO_USERNAME_LOCAL_STORAGE_KEY = "cognito_username";

/**
 * Cognito usernameを取得（リフレッシュトークン用）
 * 期限切れのトークンからでもUsernameを取得する。token cookie が消えてる場合は
 * localStorage のフォールバックを見る (refreshToken だけ残った状態からの復旧用)。
 */
export const getCognitoUsername = (): string | null => {
  // 期限切れチェックなしでトークンデータを取得
  const tokenData = getTokenDataWithoutExpCheck();

  if (tokenData) {
    const username =
      tokenData?.["cognito:username"] || tokenData?.username || tokenData?.sub;
    if (username) return username;
  }

  // [RECOVERY] token cookie が無い時のフォールバック
  try {
    return localStorage.getItem(COGNITO_USERNAME_LOCAL_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

/**
 * 現在の token cookie から username を取り出して localStorage に保存する。
 * setAuthToken(...) の直後に呼ぶ想定。
 */
export const persistCognitoUsernameForRecovery = (): void => {
  const tokenData = getTokenDataWithoutExpCheck();
  if (!tokenData) return;
  const username =
    tokenData?.["cognito:username"] || tokenData?.username || tokenData?.sub;
  if (!username) return;
  try {
    localStorage.setItem(COGNITO_USERNAME_LOCAL_STORAGE_KEY, String(username));
  } catch {
    /* noop */
  }
};

/**
 * 名前を取得
 */
export const getFirstName = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.given_name || null;
};

/**
 * 姓を取得
 */
export const getLastName = (): string | null => {
  const tokenData = getTokenData();
  return tokenData?.family_name || null;
};

/**
 * フルネームを取得
 */
export const getFullName = (): string | null => {
  const lastName = getLastName();
  const firstName = getFirstName();
  if (lastName && firstName) {
    return `${lastName} ${firstName}`;
  }
  return lastName || firstName || null;
};

/**
 * 認証状態をチェック
 */
export const isAuthenticated = (): boolean => {
  const tokenData = getTokenData();
  return tokenData !== null;
};

/**
 * すべてのトークンデータを取得（デバッグ用）
 */
export const getAllTokenData = (): TokenData | null => {
  return getTokenData();
};

/**
 * トークンをクリア
 */
export const clearToken = (): void => {
  removeAuthToken();
  removeRefreshToken();
};

export {
  clearPostLoginRedirect,
  consumePostLoginRedirect,
  savePostLoginRedirect,
} from "./postLoginRedirect";
